import React from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ExportConfiguration = ({ 
  config, 
  onConfigChange, 
  selectedCount, 
  onGenerate, 
  isGenerating 
}) => {
  const formatOptions = [
    { value: 'excel', label: 'Excel (.xlsx)' },
    { value: 'csv', label: 'CSV (.csv)' }
  ];

  const rowLimitOptions = [
    { value: 100, label: '100 registros' },
    { value: 500, label: '500 registros' },
    { value: 1000, label: '1,000 registros' },
    { value: 5000, label: '5,000 registros' },
    { value: 10000, label: '10,000 registros' },
    { value: 0, label: 'Todos los registros' }
  ];

  return (
    <div className="space-y-6">
      {/* Export Configuration */}
      <div className="bg-surface border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Settings" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">
            Configuración de Exportación
          </h3>
        </div>

        <div className="space-y-4">
          {/* Format Selection */}
          <Select
            label="Formato de Archivo"
            options={formatOptions}
            value={config?.format}
            onChange={(value) => onConfigChange('format', value)}
            required
          />

          {/* Row Limit */}
          <Select
            label="Límite de Registros"
            options={rowLimitOptions}
            value={config?.rowLimit}
            onChange={(value) => onConfigChange('rowLimit', value)}
            description="Número máximo de registros a exportar"
            required
          />

          {/* Filename */}
          <Input
            label="Nombre del Archivo"
            type="text"
            value={config?.filename}
            onChange={(e) => onConfigChange('filename', e?.target?.value)}
            placeholder="productos_mercadolibre"
            description="Sin extensión, se añadirá automáticamente"
          />

          {/* Additional Options */}
          <div className="space-y-3 pt-4 border-t border-border">
            <h4 className="font-medium text-text-primary">Opciones Adicionales</h4>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="include-headers"
                checked={config?.includeHeaders}
                onChange={(e) => onConfigChange('includeHeaders', e?.target?.checked)}
                className="h-4 w-4 text-primary"
              />
              <label htmlFor="include-headers" className="text-sm text-text-primary">
                Incluir encabezados de columna
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Selection Summary */}
      <div className="bg-surface border border-border rounded-lg p-6 shadow-soft">
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Resumen de Selección
        </h3>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Campos seleccionados:</span>
            <span className="font-semibold text-text-primary">{selectedCount}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Registros máximos:</span>
            <span className="font-semibold text-text-primary">
              {config?.rowLimit === 0 ? 'Todos' : config?.rowLimit?.toLocaleString('es-ES')}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Formato:</span>
            <span className="font-semibold text-text-primary capitalize">
              {config?.format}
            </span>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <div className="bg-surface border border-border rounded-lg p-6 shadow-soft">
        <Button
          onClick={onGenerate}
          disabled={isGenerating || selectedCount === 0}
          className="w-full"
          size="lg"
        >
          {isGenerating ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
              Generando Archivo...
            </>
          ) : (
            <>
              <Icon name="Download" size={20} className="mr-2" />
              Generar Archivo Excel
            </>
          )}
        </Button>

        {selectedCount === 0 && (
          <p className="text-sm text-error mt-2 text-center">
            Selecciona al menos un campo para continuar
          </p>
        )}

        {selectedCount > 0 && (
          <p className="text-sm text-text-secondary mt-2 text-center">
            Se generará un archivo con {selectedCount} columnas
          </p>
        )}
      </div>

      {/* MercadoLibre Compatibility */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-3">
          <Icon name="CheckCircle" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">
            Compatibilidad MercadoLibre
          </h3>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={14} className="text-success" />
            <span className="text-text-secondary">Formato compatible con bulk upload</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={14} className="text-success" />
            <span className="text-text-secondary">Campos validados automáticamente</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={14} className="text-success" />
            <span className="text-text-secondary">Optimizado para procesamiento</span>
          </div>
        </div>
      </div>

      {/* Help & Support */}
      <div className="bg-surface border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center space-x-3 mb-3">
          <Icon name="HelpCircle" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">
            ¿Necesitas Ayuda?
          </h3>
        </div>
        
        <p className="text-sm text-text-secondary mb-4">
          Revisa nuestra guía sobre selección de campos y mejores prácticas.
        </p>
        
        <div className="space-y-2">
          <Button variant="outline" size="sm" className="w-full justify-start">
            <Icon name="FileText" size={16} className="mr-2" />
            Ver Guía de Campos
          </Button>
          
          <Button variant="outline" size="sm" className="w-full justify-start">
            <Icon name="MessageCircle" size={16} className="mr-2" />
            Contactar Soporte
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportConfiguration;